/* Messages.js — debug layout version with inline styles & outlines */

import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { connect, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import { IntlProvider, FormattedDate, FormattedTime } from "react-intl";
import { NumericFormat } from "react-number-format";
import { getPredefinedMessages } from "../../actions/predefinedMessages";
import "./Messages.css";

function Messages(props) {
  const {
    incident,
    messages,
    pendingMessage,
    updatePendingMessage,
    addMessage,
    clearMessages,
    closeModal,
    closeTitle,
    predefinedMessages,
    profile,
  } = props;

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const messageRefs = useRef({});

  const reportSchemaId = incident?.report_schema_id;

  const fetchedPredefsRef = useRef(new Set());

useEffect(() => {
  if (!reportSchemaId) return;

  // If we've already fetched for this schema in this session, skip
  if (fetchedPredefsRef.current.has(reportSchemaId)) return;

  // If the store already has data, mark as fetched and skip
  if (Array.isArray(predefinedMessages?.detailsList) && predefinedMessages.detailsList.length > 0) {
    fetchedPredefsRef.current.add(reportSchemaId);
    return;
  }

  // Otherwise fetch once
  (async () => {
    try {
      const token = await getAccessTokenSilently();
      await dispatch(getPredefinedMessages(reportSchemaId, token));
      fetchedPredefsRef.current.add(reportSchemaId);
    } catch (err) {
      console.error("[Messages] Error fetching predefined messages:", err);
    }
  })();
  // ⚠️ Depend ONLY on the schema id. dispatch/getAccessTokenSilently are stable here.
}, [reportSchemaId]);
  useEffect(() => () => dispatch(clearMessages()), [dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const onClose =
    typeof closeModal === "function"
      ? closeModal
      : typeof props.hide === "function"
      ? props.hide
      : typeof props.onHide === "function"
      ? props.onHide
      : null;

  const endTitle = closeTitle || "End Chat";

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const selectPredefinedMessage = (msg) => {
    dispatch(updatePendingMessage(msg));
    setDropdownOpen(false);
  };

  const handleKeyPress = (e) => {
    if (e.code === "Enter" && !e.shiftKey) {
      e.preventDefault();
      addMessage();
      scrollToBottom();
    }
  };

  const scrollToBottom = () => {
    if (messages?.length) {
      const lastIdx = messages.length - 1;
      const lastMessage = messageRefs.current[`item-${lastIdx}`];
      if (lastMessage) {
        lastMessage.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }
  };

  // ---- DEBUG: log heights so we know what's not stretching
  useLayoutEffect(() => {
    const body = document.querySelector("#chat-modal .modal-body");
    const wrapper = document.querySelector(".messages-modal-container");
    const row = document.getElementById("messages-main");
    const right = document.getElementById("chat-messages");
    const list = document.getElementById("messages-list");
    console.log("[CHAT heights] body:", body?.clientHeight,
      " wrapper:", wrapper?.clientHeight,
      " row:", row?.clientHeight,
      " right:", right?.clientHeight,
      " list:", list?.clientHeight
    );
  });

  return (
    <div
      className="messages-modal-container"
      /* OUTER WRAPPER (magenta) — must fill modal-body */
      style={{
        outline: "3px solid magenta",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div
        className="row"
        id="messages-main"
        /* ROW (cyan) — must fill wrapper */
        style={{
          outline: "2px solid cyan",
          display: "flex",
          alignItems: "stretch",
          flex: "1 1 auto",
          minHeight: 0,
          height: "100%",
        }}
      >
        {/* LEFT COLUMN */}
        <div
          className="col-md-3 col-xs-6"
          id="messages-incident-info"
          /* LEFT COL — full-height, fixed width */
          style={{
            display: "flex",
            flexDirection: "column",
            flex: "0 0 333px",
            maxWidth: 333,
            width: 333,
            height: "100%",
            minHeight: 0,
          }}
        >
          {/* Scrollable middle content */}
          <div
            className="incident-scroll"
            style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}
          >
            <div className="incident-gutter">
              <div>{incident.reporter_name || "name:anonymous"}</div>
              <div>{incident.reporter_email || "email:anonymous"}</div>
              <div>
                <NumericFormat
                  value={incident.reporter_phone}
                  format="###-###-####"
                  displayType="text"
                />
                <div>{incident.reporter_phone || "phone:anonymous"}</div>
              </div>

              <div className="spacer20" />
              <div className="summary-display text-medium">
                {incident.display_title}
              </div>
              <div className="text-primary">{incident.title}</div>

              {incident.incident_type_translation?.en_US &&
                incident.title !== incident.incident_type_translation.en_US && (
                  <div className="text-secondary rounded bg-light border p-1">
                    {incident.incident_type_translation.en_US}
                  </div>
                )}

              <div className="spacer20" />
              <div className="text-primary">{incident.description}</div>

              {incident.incident_description_translation?.en_US &&
                incident.description !==
                  incident.incident_description_translation.en_US && (
                  <div className="text-secondary rounded bg-light border p-1">
                    {incident.incident_description_translation.en_US}
                  </div>
                )}

              <div className="spacer" />
              <div className="text-primary">{incident.location}</div>

              {incident.location_description_translation?.en_US &&
                incident.location !==
                  incident.location_description_translation.en_US && (
                  <div className="text-secondary rounded bg-light border p-1">
                    {incident.location_description_translation.en_US}
                  </div>
                )}
            </div>
          </div>

          {/* Bottom footer: dropup + End Chat */}
          <div
            className="left-rail-footer"
            style={{
              marginTop: "auto",
              display: "grid",
              gap: 8,
              background: "#fff",
              borderTop: "1px solid #dee2e6",
              position: "relative",
              zIndex: 3,
              padding: "12px 8px",
            }}
          >
            <div className="dropdown dropup" style={{ overflow: "visible" }}>
              <button
                className="btn btn-primary dropdown-toggle my-dropbtn"
                type="button"
                onClick={toggleDropdown}
                aria-expanded={dropdownOpen}
              >
                Predefined Messages
              </button>

              <ul
                className={`dropdown-menu ${dropdownOpen ? "show" : ""}`}
                data-bs-popper="static"
                style={{
                  inset: "auto 0 100% 0",
                  transform: "none",
                  maxHeight: "40vh",
                  overflow: "auto",
                  marginBottom: 8,
                  zIndex: 1060,
                }}
              >
                {predefinedMessages?.detailsList?.map((item, idx) => (
                  <li key={idx}>
                    <h6 className="dropdown-header">{item.inc_title}</h6>
                    {item.inc_type_msg.map((msg, msgIdx) => (
                      <button
                        key={msgIdx}
                        className="dropdown-item"
                        onClick={() => selectPredefinedMessage(msg)}
                        style={{
                          whiteSpace: "normal",
                          lineHeight: 1.3,
                          wordBreak: "break-word",
                        }}
                      >
                        {msg}
                      </button>
                    ))}
                  </li>
                ))}
              </ul>
            </div>

            <button
              id="end-chat-btn"
              className="btn btn-lg btn-danger end-chat"
              onClick={onClose || undefined}
              disabled={!onClose}
            >
              {endTitle}
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div
          className="col-md-6 col-xs-8"
          id="chat-messages"
          /* RIGHT COL (orange) this gets overriden in messge.css— full-height flex column */
          style={{
            outline: "2px solid #3f4b75",
            display: "grid",
            gridTemplateRows: "1fr auto",   // list fills; input sticks to bottom
            flex: "0 0 667px",
            maxWidth: 667,
            width: 667,
            height: "100%",
            minHeight: 0,
            overflow: "hidden", // keep the scroller only on the list
          }}
        >
          <ul
            className="messages-thread list-group"
            id="messages-list"
            /* SCROLLER (red) — fills remaining space and scrolls */
            style={{
              outline: "2px solid red",
              overflowY: "auto",              // the only scrollbar
              minHeight: 0,                   // lets it shrink within the grid
              marginBottom: 0,
            }}
          >
            {messages.map((msg, idx) => {
              const isNote = msg.type === "note";
            const reporterId = incident?.user_id || incident?.userId; // ground truth: who opened the report
            // Console/right if (a) it’s you OR (b) it’s anyone who is NOT the reporter
        const isResponder = !isNote && (
            msg.sender_id === profile.sub ||
            (!!reporterId && msg.sender_id !== reporterId)
            );
              return (
                <li
                  key={idx}
                  className={`list-group-item ${
                  isNote ? "note-item" : (isResponder ? "responder-item" : "reporter-item")
                  }`}
                >
                  <div
                    ref={(node) => {
                      messageRefs.current[`item-${idx}`] = node;
                    }}
                    id={
                      isNote
                        ? "note-message"
                        : isResponder
                        ? "responder-message"
                        : "reporter-message"
                    }
                  >
                    {msg.original_message || msg.message}
                    {msg.message_translation?.en_US &&
                      msg.original_message !==
                        msg.message_translation.en_US && (
                        <div className="text-secondary rounded bg-light border p-1">
                          {msg.message_translation.en_US}
                        </div>
                      )}
                    {msg.message_translation?.es_US &&
                      msg.original_message !==
                        msg.message_translation.es_US && (
                        <div className="text-secondary rounded bg-light border p-1">
                          {msg.message_translation.es_US}
                        </div>
                      )}
                  </div>
                  <IntlProvider locale="en">
  <div className="chat-meta text-small">
    <span className="chat-who">{msg.display_name}</span>
    <span className="chat-when">
      <FormattedDate value={msg.sent || ""} />{" "}
      <FormattedTime value={msg.sent || ""} />
    </span>
  </div>
</IntlProvider>
                </li>
              );
            })}
          </ul>

          <div
            className="messages-input-wrapper"
            id="chat-input"
            /* INPUT (blue) — anchored to bottom of right column */
            style={{
              outline: "none",
              background: "#fff",
              borderTop: "1px solid #dee2e6",
              paddingTop: 8
            }}
          >
            <div
              className="messages-input-group"
              style={{ display: "flex", width: "100%", alignItems: "stretch", gap: 8 }}
            >
              <textarea
                id="chat-input-box"
                className="messages-textarea"
                placeholder="Type Your Message Here"
                autoFocus
                value={pendingMessage || ""}
                onChange={(e) => dispatch(updatePendingMessage(e.target.value))}
                onKeyDown={handleKeyPress}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="messages-send-btn"
                id="chat-send-button"
                onClick={addMessage}
              >
                Send
              </button>
            </div>
          </div>
        </div>
        {/* end right column */}
      </div>
    </div>
  );
}

Messages.propTypes = {
  incident: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  clearMessages: PropTypes.func.isRequired,
  updatePendingMessage: PropTypes.func.isRequired,
  addMessage: PropTypes.func.isRequired,
  closeModal: PropTypes.func,
  closeTitle: PropTypes.string,
  predefinedMessages: PropTypes.object,
  profile: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  pendingMessage:
    state.messages?.pendingMessage ||
    state.internalMessages?.pendingMessage ||
    "",
  profile: state.profile || {},
  // ✅ Always provide a stable shape
  predefinedMessages:
    state.predefinedMessages && Array.isArray(state.predefinedMessages.detailsList)
      ? state.predefinedMessages
      : { detailsList: [] },
});


export default connect(mapStateToProps)(Messages);
